import { useState, useCallback, useMemo, useEffect } from "react";
import { Box, Typography, AppBar, Toolbar } from "@mui/material";
import CoreButtons from "../../components/core-components/button/CoreButtons";
import ConfirmationModal from "../../components/reusable-components/modals/confirmation-modal/ConfirmationModal";
import AddQuoteImagesModal from "../../components/reusable-components/modals/AddQuoteImagesModal/AddQuoteImagesModal";
import PhotoLibraryModal from "../../components/reusable-components/modals/photo-ibrary-modal/PhotoLibraryModal";
import PreviewModal from "../../components/reusable-components/modals/preview-modal/PreviewModal";
import {
	ICreateQuoteFormSection,
	IQuoteFormMenuAndServicesInfo,
	IQuoteFormStep,
} from "../../types/quoteFormTypes";
import { BUTTON_COLOR, BUTTON_VARIANT } from "../../types/coreButtonTypes";
import QuoteFormStepper from "../../components/pages-components/quote-form/quote-form-stepper/QuoteFormStepper";
import QuoteFromMobileStepper from "../../components/pages-components/quote-form/quote-form-mobile-stepper/QuoteFormMobileStepper";
import IsMobile from "../../hooks/isMobile";
import YoucaterLogoSvg from "../../assets/svg-fc/YoucaterLogoSvg";
import DialogCrossCircle from "../../components/reusable-components/dialog-cross-circle/DialogCrossCircle";
import { useLocation, useNavigate } from "react-router-dom";
import { IQuoteFormFinancialDetails } from "../../types/quoteFormTypes";
import { QUOTE_FORM_FINANCIAL_SCHEMA } from "../../constants/formConstants";
import QuoteDetailsForm from "../../components/pages-components/quote-form/quote-details-form/QuoteDetailsForm";
import MenuAndServicesInfoForm from "../../components/pages-components/quote-form/menu-and-services-info-form/MenuAndServicesInfoForm";
import BackIcon from "../../assets/svg-fc/BackIcon";
import { generateQuotePreviewHTML } from "../../utils/quotePreviewHelpers";
import { submitCorporateEventBidThunk } from "../../store/b2b/corporateEvent/corporateEventThunk";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import { useToast } from "../../context/ToasterContext";
import {
	isQuoteDetailsFormInvalid,
	isQuoteFormEmpty,
	serializeSectionsContent,
	isMenuAndServicesFormInvalid,
	calculateQuoteDetails,
	checkIfAllSectionsHaveAttachments,
	getUserQuotedAmount,
	htmlToSlateConfig,
} from "../../utils/quoteFormUtils";
import { getEventDetailThunk, respondToEventFilterThunk, saveBidThunk } from "../../store/events/eventThunk";
import { iUserDetails } from "../../types/userType";
import { ValidationError } from "yup";
import { ROUTE_PATHS, ROUTE_QUERY_CONSTANTS } from "../../routes";
import { setShowThankYouModal } from "../../store/events/eventSlice";
import { useWidth } from "../../hooks/useResponsive";
import { EventDetailTabsEnum, QUOTE_TAB_VALUE } from "../../constants/eventDetail";
import { REDUX_REQUEST_STATUS } from "../../types/commonTypes";
import { getPhotoLibrarySectionsThunk } from "../../store/b2b/photoLibrary/photoLibraryThunk";
import {
	RESPOND_TO_EVENT_TYPES,
	iRespondEventPayload,
	iEventType,
	iBidType,
	MUNICIPALITY_TAX_INCLUSIVE_STATUSES,
	VendorEventResponseStatusesEnum,
} from "../../types/eventTypes";
import { ICorporateEventBid } from "../../interfaces/corporateEvent";
import { EventTypeEnum, SectionTypeEnum } from "../../constants/enums";
import { htmlToSlate } from "@slate-serializers/html";
import SaveIcon from "../../assets/svg-fc/SaveIcon";
import SelectPreviousQuoteModal from "../../components/pages-components/quote-form/select-previous-quote-modal/SelectPreviousQuoteModal";
import ConfirmCopyModal from "../../components/pages-components/quote-form/confirm-copy-modal/ConfirmCopyModal";
import "./quoteForm.scss";
import FullPagePhotoViewer from "../../components/reusable-components/FullPagePhotoViewer/FullPagePhotoViewer";
import { IPhotoLibraryPhoto } from "../../interfaces/photoLibrary";

export interface IQuoteDetailsFormTouchedState {
	amount: boolean;
	municipalityTax: boolean;
}

export interface IQuoteDetailsFormErrorsState {
	amount: string;
	municipalityTax: string;
}

const QuoteForm = () => {
	const isMobile = IsMobile();
	const isExtraSmallScreen = useWidth() === "xs";
	const navigate = useNavigate();
	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
	const dispatch = useAppDispatch();
	const { showToast } = useToast();
	const { userDetails } = useAppSelector((state) => state.user);
	const { totalBidsCount } = useAppSelector((state) => state.quotes);
	const eventDetail = useAppSelector((state) => state.events.event) || {};
	const { photoLibrarySections } = useAppSelector((state) => state.photoLibrary);
	const [existingQuote, setExistingQuote] = useState<iBidType | ICorporateEventBid>(location?.state?.quote);
	const [currentPage, setCurrentPage] = useState(1);
	const [showSelectPreviousQuoteModal, setShowSelectPreviousQuoteModal] = useState(false);
	const [showConfirmCopyModal, setShowConfirmCopyModal] = useState(false);
	const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
	const [showCautionModal, setShowCautionModal] = useState(false);
	const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(false);
	const [showPreviewModal, setShowPreviewModal] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [isAIExtracting, setIsAIExtracting] = useState<boolean>(false);
	const [uploadProgress, setUploadProgress] = useState<number>(0);
	const [pagesExtracted, setPagesExtracted] = useState<number>(0);
	const [showPhotoLibrary, setShowPhotoLibrary] = useState(false);
	const [previewPhoto, setPreviewPhoto] = useState<IPhotoLibraryPhoto | null>(null);
	const [activeSectionForPhotos, setActiveSectionForPhotos] = useState<number | "services" | null>(null);
	const eventId = queryParams?.get("eventId") as string;
	const corporateEventId = queryParams?.get("corporateEventId") as string;

	// Financial form state
	const [financialFormData, setFinancialFormData] = useState<IQuoteFormFinancialDetails>({
		amount: "",
		isMunicipalityTaxInclusive: null,
		municipalityTax: "",
		isVatInclusive: null,
		userQuoteAmount: "",
	});

	// Menu & Services form state
	const [menuFormData, setMenuFormData] = useState<IQuoteFormMenuAndServicesInfo>({
		sections: [],
	});

	// Service Section state
	const [servicesInfoSection, setServicesInfoSection] = useState<ICreateQuoteFormSection | null>(null);

	const [touchedFields, setTouchedFields] = useState<IQuoteDetailsFormTouchedState>({
		amount: false,
		municipalityTax: false,
	});

	const [formErrors, setFormErrors] = useState<IQuoteDetailsFormErrorsState>({
		amount: "",
		municipalityTax: "",
	});

	const setExistingQuoteData = useCallback(
		(isFinancialDataChecked: boolean, checkedSections: string[]) => {
			if (existingQuote) {
				if (isFinancialDataChecked) {
					setFinancialFormData({
						amount: getUserQuotedAmount(existingQuote),
						isMunicipalityTaxInclusive: existingQuote.isMunicipalityTaxInclusive,
						municipalityTax: existingQuote.municipalityTax,
						isVatInclusive: existingQuote.isVatInclusive,
						userQuoteAmount: existingQuote.amount,
					});
					handleFieldTouched("amount");
				}

				if (checkedSections?.length > 0) {
					const existingQuoteSections = existingQuote?.sections?.map((section) => {
						return {
							...section,
							content: JSON.stringify(htmlToSlate(section.content, htmlToSlateConfig)),
						};
					});

					const serviceSection = existingQuoteSections?.find(
						(section) => section.sectionType === SectionTypeEnum.SERVICE,
					);

					if (serviceSection && checkedSections?.includes(serviceSection?.id)) {
						setServicesInfoSection(serviceSection);
					}

					const menuSection = existingQuoteSections?.filter(
						(section) => section.sectionType === SectionTypeEnum.MENU,
					);

					if (menuSection?.length) {
						setMenuFormData({
							sections: menuSection.filter((section) => checkedSections.includes(section.id)),
						});
					}
				}
			}
		},
		[existingQuote],
	);

	useEffect(() => {
		if (financialFormData.isMunicipalityTaxInclusive === MUNICIPALITY_TAX_INCLUSIVE_STATUSES.NotApplicable) {
			onChangeFinancialValue("", "municipalityTax");
		}
	}, [financialFormData.isMunicipalityTaxInclusive]);

	useEffect(() => {
		setExistingQuoteData(true, existingQuote?.sections?.map((section) => section.id) ?? []);
	}, []);

	useEffect(() => scrollToTop(), [currentPage]);

	useEffect(() => {
		if (eventId) {
			dispatch(getEventDetailThunk({ id: eventId }));
		}
	}, [eventId]);

	useEffect(() => {
		if (eventId || corporateEventId) {
			dispatch(getPhotoLibrarySectionsThunk());
		}
	}, [eventId, corporateEventId, dispatch]);

	const progressSteps: IQuoteFormStep[] = useMemo(
		() => [
			{
				step: 1,
				title: isExtraSmallScreen ? "Quote<br>details" : "Quote details",
				isActive: currentPage === 1,
				isCompleted: currentPage > 1,
			},
			{
				step: 2,
				title: "Menu and Services Information",
				isActive: currentPage === 2,
				isCompleted: false,
			},
		],
		[currentPage, isExtraSmallScreen],
	);

	const onChangeFinancialValue = async (value: string | number, key: keyof IQuoteFormFinancialDetails) => {
		const newData = {
			...financialFormData,
			[key]: value,
		};
		setFinancialFormData(newData);
		await validateFinancialForm(newData);
	};

	const validateFinancialForm = async (formData: IQuoteFormFinancialDetails) => {
		try {
			await QUOTE_FORM_FINANCIAL_SCHEMA.validate(formData, { abortEarly: false });
			setFormErrors({ amount: "", municipalityTax: "" });
		} catch (validationError: any) {
			const errors = { amount: "", municipalityTax: "" };

			if (validationError.inner) {
				validationError.inner.forEach((err: ValidationError) => {
					switch (err.path) {
						case "amount":
							errors.amount = err.message;
							break;
						case "municipalityTax":
							errors.municipalityTax = err.message;
							break;
					}
				});
			}

			setFormErrors(errors);
		}
	};

	const handleFieldTouched = (key: keyof IQuoteFormFinancialDetails) => {
		setTouchedFields({
			...touchedFields,
			[key]: true,
		});
	};

	const handleNext = useCallback(async () => {
		if (currentPage === 1) {
			// Mark all fields as touched to show validation errors
			setTouchedFields({
				amount: true,
				municipalityTax: true,
			});

			// Validate the form before proceeding
			await validateFinancialForm(financialFormData);

			// Wait for validation to complete and check for errors
			// We need to validate again to get the current errors since state updates are async
			try {
				await QUOTE_FORM_FINANCIAL_SCHEMA.validate(financialFormData, { abortEarly: false });
				// No validation errors, proceed to next page
				setCurrentPage(2);
			} catch (validationError) {
				// Validation failed, errors will be shown via the validateFinancialForm function
				// Don't proceed to next page
			}
		}
	}, [currentPage, financialFormData]);

	const handleBack = () => {
		if (currentPage === 1) {
			onClose();
		} else {
			setCurrentPage(1);
		}
	};

	const handleConfirmSubmit = () => {
		setShowSubmitConfirmation(false);
		setIsLoading(true);

		const quoteFormData = {
			...financialFormData,
			amount: Number(calculatedValues.subTotal),
			municipalityTax: Number(financialFormData.municipalityTax),
			userQuoteAmount: Number(Number(financialFormData.amount).toFixed(2)),
			...serializeSectionsContent(menuFormData, servicesInfoSection),
		};

		const submitBid = eventId
			? dispatch(saveBidThunk({ eventId, ...quoteFormData }))
			: dispatch(submitCorporateEventBidThunk({ corporateEventId, ...quoteFormData }));

		submitBid
			.unwrap()
			.then((res) => {
				if (res) {
					corporateEventId && showToast("Quote submitted successfully");
					eventId && eventDetail?.vendorStatus === 9
						? eventActionHandler(RESPOND_TO_EVENT_TYPES.ACCEPT)
						: goBack();
					eventId && dispatch(setShowThankYouModal(true));
				}
			})
			.catch((err) => {
				showToast(err.message, undefined, "error");
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	// For B2C Event
	const eventActionHandler = async (status: number) => {
		const payload = {
			id: eventId,
			status: status,
			declineReason: { value: "" },
			otherDeclineReason: "",
			firstVendorInteraction: eventDetail?.vendorStatus === 9,
		} as iRespondEventPayload;

		const resp = await dispatch(respondToEventFilterThunk(payload));

		if (resp.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED) {
			const eventDetailResp = await dispatch(getEventDetailThunk({ id: eventId }));
			if (eventDetailResp.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED) {
				const eventDetail = eventDetailResp.payload as iEventType;

				if (
					(eventDetail?.vendorStatus === VendorEventResponseStatusesEnum.AvailableForBidding ||
						eventDetail?.vendorStatus === VendorEventResponseStatusesEnum.Invited ||
						eventDetail?.vendorStatus === VendorEventResponseStatusesEnum.AssignedByAdmin ||
						eventDetail?.vendorStatus === VendorEventResponseStatusesEnum.Bidding) &&
					status === RESPOND_TO_EVENT_TYPES.ACCEPT
				) {
					navigate(
						{
							pathname: `${ROUTE_PATHS.EVENT_DETAIL.replace(":id", eventId)}`,
							search: `?type=${ROUTE_QUERY_CONSTANTS.InProgress}&tab=${EventDetailTabsEnum.MyQuote}`,
						},
						{ replace: true },
					);
				}
			}
		}
	};

	const generatePreviewHTML = useCallback(() => {
		return generateQuotePreviewHTML(
			financialFormData,
			serializeSectionsContent(menuFormData, servicesInfoSection),
			userDetails as iUserDetails,
			true,
			corporateEventId ? true : false,
		);
	}, [menuFormData, financialFormData, servicesInfoSection]);

	// Memoize the HTML content to avoid generating it on every render
	const previewHTMLContent = useMemo(() => {
		if (!showPreviewModal) return "";
		return generatePreviewHTML();
	}, [showPreviewModal, generatePreviewHTML]);

	const handleConfirmClose = () => {
		setShowCloseConfirmation(false);
		goBack();
	};

	const scrollToTop = () => {
		const container = document.querySelector(".quoteFormContainer");
		if (container) {
			container.scrollTo({ top: 0, behavior: "smooth" });
		}
	};

	const goBack = () => {
		if (window.history.length > 1) {
			if (eventId) {
				return navigate(
					{
						pathname: `${ROUTE_PATHS.EVENT_DETAIL.replace(":id", eventId)}`,
						search: `?type=${ROUTE_QUERY_CONSTANTS.InProgress}&tab=${EventDetailTabsEnum.MyQuote}`,
					},
					{ replace: true },
				);
			}
			navigate(-1);
		} else {
			if (eventId) {
				navigate(ROUTE_PATHS.EVENT_DETAIL.replace(":id", eventId as string));
			} else {
				navigate(ROUTE_PATHS.B2B.EVENT_DETAIL.replace(":id", corporateEventId as string));
			}
		}
	};

	const onClose = () => {
		if (isQuoteFormEmpty(financialFormData, menuFormData)) {
			goBack();
		} else {
			setShowCloseConfirmation(true);
		}
	};

	const calculatedValues = useMemo(() => {
		return calculateQuoteDetails(financialFormData);
	}, [financialFormData]);

	const selectedAttachments = useMemo(() => {
		if (activeSectionForPhotos === "services") {
			return servicesInfoSection?.attachments
				? servicesInfoSection?.attachments?.map((attachment) => attachment ?? "")
				: [];
		} else if (activeSectionForPhotos !== null) {
			const section = menuFormData.sections.find((s) => s.displayOrder === activeSectionForPhotos);
			return section?.attachments ? section?.attachments?.map((attachment) => attachment ?? "") : [];
		}
		return [];
	}, [servicesInfoSection, menuFormData.sections, activeSectionForPhotos, showPhotoLibrary]);

	return (
		<>
			<Box className="quoteFormContainer">
				{/* Header */}
				<AppBar className="dialogAppBar">
					<Toolbar className="dialogToolBar">
						<YoucaterLogoSvg />
						<DialogCrossCircle onClose={onClose} />
					</Toolbar>
				</AppBar>

				<Box className="formTitleContainer">
					{isMobile && (
						<Box
							className={`backIcon ${isAIExtracting ? "disabled" : ""}`}
							onClick={() => !isAIExtracting && handleBack()}
						>
							<BackIcon />
						</Box>
					)}
					<Typography className="formTitle">New Quote</Typography>
				</Box>

				<Box className="quoteFormContent">
					{/* Progress Indicator */}

					{isMobile ? (
						<QuoteFromMobileStepper
							steps={progressSteps}
							disabled={isAIExtracting}
							onClickStep={setCurrentPage}
						/>
					) : (
						<QuoteFormStepper steps={progressSteps} disabled={isAIExtracting} onClickStep={setCurrentPage} />
					)}

					{/* Main Form Content */}
					<Box className="quoteForm">
						{totalBidsCount && totalBidsCount > 0 ? (
							<Box className="copyFromPreviousQuoteContainer">
								<Box className="copyFromPreviousQuoteContent">
									<SaveIcon />
									<Typography className="copyFromPreviousQuoteText">
										Save time by copying from a previous quote you submitted
									</Typography>
								</Box>
								<CoreButtons
									className="selectQuoteBtn"
									buttonText="Select quote"
									variant={BUTTON_VARIANT.OUTLINED}
									color={BUTTON_COLOR.SECONDARY}
									coreBtnPill={true}
									onclick={() => setShowSelectPreviousQuoteModal(true)}
								/>
							</Box>
						) : null}

						{currentPage === 1 ? (
							/* Step 1: Quote Details Form */
							<QuoteDetailsForm
								financialFormData={financialFormData}
								handleFieldTouched={handleFieldTouched}
								onChangeFinancialValue={onChangeFinancialValue}
								touchedFields={touchedFields}
								formErrors={formErrors}
								calculatedValues={calculatedValues}
							/>
						) : (
							/* Step 2: Menu & Services Form */
							<MenuAndServicesInfoForm
								menuFormData={menuFormData}
								servicesInfoSection={servicesInfoSection as ICreateQuoteFormSection}
								selectedFile={selectedFile}
								isAIExtracting={isAIExtracting}
								uploadProgress={uploadProgress}
								pagesExtracted={pagesExtracted}
								setMenuFormData={setMenuFormData}
								setServicesInfoSection={setServicesInfoSection}
								setSelectedFile={setSelectedFile}
								setIsAIExtracting={setIsAIExtracting}
								setUploadProgress={setUploadProgress}
								setPagesExtracted={setPagesExtracted}
								setShowPhotoLibrary={(sectionIdentifier: number | "services") => {
									// empty photo library section
									setActiveSectionForPhotos(sectionIdentifier);
									setShowPhotoLibrary(true);
								}}
							/>
						)}
					</Box>
				</Box>

				{/* Footer */}
				<Box className="quoteFormFooter">
					{!isMobile && (
						<CoreButtons
							buttonText="Back"
							variant={BUTTON_VARIANT.OUTLINED}
							color={BUTTON_COLOR.SECONDARY}
							onclick={handleBack}
							disabled={isAIExtracting}
							className="quoteFormFooterBtn"
						/>
					)}

					<Box className="quoteFormFooterRightContainer">
						{currentPage === 2 &&
							!isAIExtracting &&
							!isMenuAndServicesFormInvalid(menuFormData, servicesInfoSection) && (
								<CoreButtons
									buttonText="Preview"
									variant={BUTTON_VARIANT.OUTLINED}
									onclick={() => setShowPreviewModal(true)}
									disabled={isLoading}
									className="quoteFormFooterBtn"
								/>
							)}

						{currentPage === 1 ? (
							<CoreButtons
								buttonText="Next"
								variant={BUTTON_VARIANT.CONTAINED}
								onclick={handleNext}
								disabled={isQuoteDetailsFormInvalid(touchedFields, formErrors, financialFormData)}
								className="quoteFormFooterBtn quoteFormFooterNextBtn"
							/>
						) : (
							<CoreButtons
								buttonText="Submit"
								variant={BUTTON_VARIANT.CONTAINED}
								onclick={() =>
									checkIfAllSectionsHaveAttachments(menuFormData.sections)
										? setShowSubmitConfirmation(true)
										: setShowCautionModal(true)
								}
								disabled={isMenuAndServicesFormInvalid(menuFormData, servicesInfoSection) || isAIExtracting}
								className="quoteFormFooterBtn"
								loading={isLoading}
							/>
						)}
					</Box>
				</Box>
			</Box>

			{/* Select Previous Quote Modal */}
			<SelectPreviousQuoteModal
				eventId={eventId || corporateEventId}
				eventType={corporateEventId ? EventTypeEnum.CORPORATE_EVENT : EventTypeEnum.CUSTOM_EVENT}
				open={showSelectPreviousQuoteModal}
				selectedBidId={existingQuote?.id}
				onBidClick={(bid) => {
					setShowConfirmCopyModal(true);
					setExistingQuote(bid);
				}}
				onClose={() => setShowSelectPreviousQuoteModal(false)}
			/>

			{/* Confirm Copy Modal */}
			<ConfirmCopyModal
				open={showConfirmCopyModal}
				sections={existingQuote?.sections ?? []}
				onClose={() => setShowConfirmCopyModal(false)}
				onConfirm={setExistingQuoteData}
			/>

			{/* Confirmation Modals */}
			<ConfirmationModal
				open={showCloseConfirmation}
				heading="Close form"
				description="Are you sure you want to close this form, all your progress will be lost?"
				buttonText="Close form"
				buttonColor={BUTTON_COLOR.ERROR}
				onClose={() => setShowCloseConfirmation(false)}
				onClickSave={handleConfirmClose}
			/>

			<AddQuoteImagesModal
				open={showCautionModal}
				onClose={() => setShowCautionModal(false)}
				setShowPhotoLibrary={() => {
					setShowCautionModal(false);
					setTimeout(() => {
						const section = menuFormData.sections.find((s) => s.attachments?.length === 0);
						setActiveSectionForPhotos(section?.displayOrder as number | "services");
						setShowPhotoLibrary(true);
					}, 100);
				}}
			/>

			<PhotoLibraryModal
				open={showPhotoLibrary}
				triggerPreviewPhoto={setPreviewPhoto}
				selectedAttachments={selectedAttachments ?? []}
				onClose={() => {
					setShowPhotoLibrary(false);
					setActiveSectionForPhotos(null);
				}}
				onAttachmentsChange={(attachments) => {
					if (activeSectionForPhotos === "services") {
						setServicesInfoSection({
							...(servicesInfoSection as ICreateQuoteFormSection),
							attachments: attachments,
						});
					} else if (activeSectionForPhotos !== null) {
						setMenuFormData({
							...menuFormData,
							sections: menuFormData.sections.map((section) =>
								section.displayOrder === activeSectionForPhotos
									? { ...section, attachments: attachments }
									: section,
							),
						});
					}
				}}
			/>

			<ConfirmationModal
				open={showSubmitConfirmation}
				heading="Submit Quote"
				description="This quote will be shared with the customer. Once you click Submit, this action cannot be undone."
				buttonText="Confirm"
				onClose={() => setShowSubmitConfirmation(false)}
				onClickSave={handleConfirmSubmit}
			/>

			<PreviewModal
				open={showPreviewModal}
				onClose={() => setShowPreviewModal(false)}
				htmlContent={previewHTMLContent}
				title="Preview"
			/>

			{previewPhoto && (
				<FullPagePhotoViewer
					previewPhoto={previewPhoto}
					setPreviewPhoto={setPreviewPhoto}
					photoLibrarySections={photoLibrarySections ?? []}
				/>
			)}
		</>
	);
};

export default QuoteForm;
